import {PlatformLocation, APP_BASE_HREF, LOCATION_INITIALIZED} from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes} from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConsoleLoggerService } from 'src/util/console-logger.service';
import { Logger } from 'src/util/logger';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConstants } from './AppConstants';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { ConfigureTotpComponent } from './configure-totp/configure-totp.component';
import { DukeAlertComponent } from './duke-alert/duke-alert.component';
import { EditProfileComponent } from './edit-profile/edit-profile.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { FormInputFeedbackComponent } from './form-input-feedback/form-input-feedback.component';
import { FormInputComponent } from './form-input/form-input.component';
import { LoginComponent } from './login/login.component';
import { RegisterComponent } from './register/register.component';
import { ValidateEmailComponent } from './validate-email/validate-email.component';
import { ViewProfileComponent } from './view-profile/view-profile.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { LoggerErrorHandler } from './LoggerErrorHandler';
import { CanDeactivateGuardService } from './can-deactivate-guard.service';
import { LocaleService } from './locale.service';
import { AlertHandler, NoConcurrentAlertsHandler } from './no-concurrent-alerts-handler';
import { LogoComponent } from './logo/logo.component';
import {ClickOutsideDirective} from './click-outside.directive';
import { LogoutComponent } from './logout/logout.component';
import { JoinComponent } from './join/join.component';
import {getAppBaseHref, HttpLoaderFactory, getLocale} from '../util/locationUtil';
import { BsSampleComponent } from './bs-sample/bs-sample.component';
import {NotWithSessionGuardService} from './not-with-session-guard.service';
import {NotWithoutSessionGuardService} from './not-without-session-guard.service';
import {HomeComponent} from './home/home.component';

const appRoutes: Routes = [
  {
    path: AppConstants.ROUTE_REGISTER,
    component: RegisterComponent,
    canActivate: [NotWithSessionGuardService],
    data: { animation: 'Register' },
  },
  {
    path: AppConstants.ROUTE_VALIDATE_EMAIL,
    component: ValidateEmailComponent,
    data: { animation: 'ValidateEmail' },
  },
  {
    path: AppConstants.ROUTE_FORGOT_PASSWORD,
    component: ForgotPasswordComponent,
    canActivate: [NotWithSessionGuardService],
    data: { animation: 'ForgotPassword' },
  },
  {
    path: AppConstants.ROUTE_RESET_PASSWORD,
    component: LoginComponent,
    canActivate: [NotWithSessionGuardService],
    data: { animation: 'ResetPassword' },
  },
  {
    path: AppConstants.ROUTE_LOGIN,
    component: LoginComponent,
    runGuardsAndResolvers: 'always',
    canActivate: [NotWithSessionGuardService],
    data: { animation: 'Login' },
  },
  {
    path: AppConstants.ROUTE_LOGOUT,
    component: LogoutComponent,
    data: { animation: 'Logout' },
  },
  {
    path: AppConstants.ROUTE_PROFILE,
    component: ViewProfileComponent,
    canActivate: [NotWithoutSessionGuardService],
    data: { animation: 'ViewProfile' },
  },
  {
    path: AppConstants.ROUTE_EDIT_PROFILE,
    component: EditProfileComponent,
    canActivate: [NotWithoutSessionGuardService],
    canDeactivate: [CanDeactivateGuardService],
    data: { animation: 'EditProfile' },
  },
  {
    path: AppConstants.ROUTE_CHANGE_PASSWORD,
    component: ChangePasswordComponent,
    canActivate: [NotWithoutSessionGuardService],
    data: { animation: 'ChangePassword' },
  },
  {
    path: AppConstants.ROUTE_CONFIGURE_TOTP,
    component: ConfigureTotpComponent,
    canActivate: [NotWithoutSessionGuardService],
    data: { animation: 'ConfigureTotp' },
  },
  {
    path: AppConstants.ROUTE_JOIN,
    component: JoinComponent,
    data: { animation: 'Join' },
  },
  {
    path: AppConstants.ROUTE_HOME,
    component: HomeComponent,
    canActivate: [NotWithSessionGuardService, NotWithoutSessionGuardService],
    data: { animation: 'Home' },
  },
  {
    path: '**',
    component: PageNotFoundComponent,
    data: { animation: 'NotFound' },
  },
];
if (environment.production !== true) {
  appRoutes.unshift({
    path: AppConstants.ROUTE_BS_SAMPLE,
    component: BsSampleComponent,
    data: { animation: 'BS_SAMPLE' },
  });
}

export function appInitializerFactory(localeService: LocaleService, translate: TranslateService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    // not sure what this is, docs say it "Indicates when a location is initialized."
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      translate.setDefaultLang('en');
      const locale = getLocale();
      // check if any locale was requested
      if (locale) {
        localeService.setLocaleParam('locale');
        // try using requested locale
        translate.use(locale).subscribe(res => {
          // success, all done.
          localeService.setIsAvailable(true);
          resolve(null);
        }, e => {
          // requested locale not supported, check if language is available without the country part, if country part is found
          if (locale.indexOf('-') > 0) {
            translate.use(locale.split('-')[0]).subscribe(res => {
              // success, all done.
              localeService.setIsAvailable(true);
              resolve(null);
            }, err => {
              // failed, locale not supported. Leave in path for future support and show notification
              localeService.setIsAvailable(false);
              resolve(null);
            });
          } else {
            // failed, locale not supported. Leave in path for future support and show notification
            localeService.setIsAvailable(false);
            resolve(null);
          }
        });
      } else {
        // no locale was requested, continue with default.
        localeService.setLocaleParam(null);
        localeService.setIsAvailable(true);
        resolve(null);
      }
    });
  });
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    ValidateEmailComponent,
    ForgotPasswordComponent,
    EditProfileComponent,
    ViewProfileComponent,
    DukeAlertComponent,
    FormInputComponent,
    FormInputFeedbackComponent,
    ChangePasswordComponent,
    ConfigureTotpComponent,
    PageNotFoundComponent,
    LogoComponent,
    ClickOutsideDirective,
    LogoutComponent,
    JoinComponent,
    HomeComponent,
    BsSampleComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    environment.sessionApi,
    NgbModule,
    RouterModule.forRoot(
      appRoutes,
      { enableTracing: false,
        onSameUrlNavigation: 'reload'}
    ),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, PlatformLocation]
      }
    }),
  ],
  providers: [
    { provide: Logger, useClass: ConsoleLoggerService },
    { provide: AlertHandler, useClass: NoConcurrentAlertsHandler },
    CanDeactivateGuardService,
    NotWithSessionGuardService,
    NotWithoutSessionGuardService,
    { provide: ErrorHandler, useClass: LoggerErrorHandler },
    { provide: APP_BASE_HREF, useFactory: getAppBaseHref, deps: [PlatformLocation] },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [LocaleService, TranslateService, Injector],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})

export class AppModule { }

